import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Truck, MapPin, Radio, Navigation, AlertTriangle } from "lucide-react";

export default function DriverCheckin() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(0);
  const watchId = useRef(null);
  const lastSent = useRef(0);

  useEffect(() => {
    api.get(`/p/driver/${token}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
    return () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); };
  }, [token]);

  async function ping(pos) {
    const now = Date.now();
    if (now - lastSent.current < 8000) return; // throttle to ~8s
    lastSent.current = now;
    try {
      await api.post(`/p/driver-ping/${token}`, {
        lat: pos.coords.latitude, lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy, speed: pos.coords.speed, heading: pos.coords.heading,
      });
      setSent((n) => n + 1);
      setStatus(`Location shared · ${new Date().toLocaleTimeString()}`);
    } catch { setStatus("Couldn't reach server — will retry"); }
  }

  function start() {
    if (!navigator.geolocation) { setError("This phone doesn't support location sharing."); return; }
    setError("");
    setSharing(true);
    watchId.current = navigator.geolocation.watchPosition(
      ping,
      (e) => { setError(e.code === 1 ? "Please allow location access to share your position." : "Couldn't get location. Check GPS is on."); setSharing(false); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
  }
  function stop() {
    if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
    watchId.current = null;
    setSharing(false);
    setStatus("Sharing stopped.");
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0E0E10] text-white flex items-center justify-center p-6">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-[#C84B31] mx-auto mb-3" />
          <p className="text-lg font-semibold">This tracking link is no longer valid.</p>
          <p className="text-white/60 text-sm mt-1">Please ask your dispatcher for a new link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white flex flex-col" data-testid="driver-checkin-page">
      <div className="px-6 pt-10 pb-6 text-center border-b border-white/10">
        <div className="inline-flex items-center gap-2 text-[#C84B31] font-bold tracking-wide"><Truck className="w-5 h-5" /> ITO Live Tracking</div>
        <h1 className="mt-4 text-3xl font-bold font-mono">{info?.vehicle_number || "…"}</h1>
        {info?.active_lr?.lr_number && (
          <p className="mt-2 text-white/70 text-sm flex items-center justify-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#C84B31]" /> {info.active_lr.lr_number} · {info.active_lr.route}
          </p>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        <div className={`w-40 h-40 rounded-full flex items-center justify-center transition-all ${sharing ? "bg-[#1f8a4c]/20 animate-pulse" : "bg-white/5"}`}>
          <div className={`w-28 h-28 rounded-full flex items-center justify-center ${sharing ? "bg-[#1f8a4c]" : "bg-white/10"}`}>
            {sharing ? <Radio className="w-12 h-12 text-white" /> : <Navigation className="w-12 h-12 text-white/60" />}
          </div>
        </div>

        {error && <p className="text-[#ff8a80] text-sm text-center max-w-xs" data-testid="driver-error">{error}</p>}
        {sharing && <p className="text-[#7ee2a8] text-sm text-center" data-testid="driver-status">{status || "Getting your location…"} {sent > 0 && `· ${sent} updates sent`}</p>}
        {!sharing && !error && <p className="text-white/50 text-sm text-center max-w-xs">Tap below and keep this page open while you drive. Your dispatcher will see your live location.</p>}

        {sharing ? (
          <button onClick={stop} data-testid="driver-stop-btn" className="px-8 py-4 rounded-full bg-white/10 border border-white/20 font-semibold hover:bg-white/15">
            Stop sharing
          </button>
        ) : (
          <button onClick={start} data-testid="driver-start-btn" className="px-10 py-4 rounded-full bg-[#C84B31] font-bold text-lg hover:bg-[#a83c26] shadow-lg shadow-[#C84B31]/30">
            Share my live location
          </button>
        )}
      </div>

      <p className="text-center text-white/30 text-xs pb-6 px-6">Your location is only shared with your transport company while this page is open.</p>
    </div>
  );
}
